import { useState } from 'react';
import { Link } from 'react-router';

import { clientSubtitle, type Client } from '@/shared/clients/types';
import { useClient, useClientList } from '@/shared/clients/useClients';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { CopyIcon, SearchIcon, XIcon } from '@/shared/ui/icons';
import { useDebounced } from '@/shared/utils/useDebounced';

import { QuoteStatusBadge } from './QuoteStatusBadge';
import { type Quote } from './types';
import { useCopyForClients, useQuoteCopies } from './useQuotes';

/**
 * One quote, several customers (contract v10.8).
 *
 * The lines are prepared once and handed out: «Copiar para otros clientes» asks for the
 * customers and the API answers with one identical draft per customer, each to be
 * approved on its own so each spends its own number and renders its own PDF. This card
 * is the list of those drafts (and of the redos, which share the same link back) and
 * the button that makes more. It exists on every quote but a cancelled one: a draft is
 * handed out as much as a sent one.
 */
export function CopiesCard({ quote, className }: { quote: Quote; className?: string }) {
  const copies = useQuoteCopies(quote.id);
  const [choosing, setChoosing] = useState(false);
  const [made, setMade] = useState<Quote[] | null>(null);
  const items = copies.data?.items ?? [];
  const taken = new Set(
    items.filter((each) => each.status === 'draft').map((each) => each.client_id ?? ''),
  );

  return (
    <Card
      title="Para otros clientes"
      aside={
        <Button
          variant="secondary"
          size="sm"
          icon={<CopyIcon size={15} />}
          onClick={() => setChoosing(true)}
        >
          Copiar para otros clientes
        </Button>
      }
      {...(className ? { className } : {})}
    >
      {made ? (
        <Callout
          tone="ok"
          compact
          role="status"
          action={
            <Button variant="ghost" size="sm" onClick={() => setMade(null)}>
              Cerrar
            </Button>
          }
        >
          {made.length === 1
            ? 'Creado un borrador para ese cliente.'
            : `Creados ${made.length} borradores, uno por cliente.`}{' '}
          Cada uno se aprueba por separado.
        </Callout>
      ) : null}

      {copies.isPending ? <Spinner label="Buscando las copias" /> : null}
      {copies.isError ? (
        <ErrorMessage error={copies.error}>No se pudieron cargar las copias</ErrorMessage>
      ) : null}

      {copies.data && items.length === 0 ? (
        <p className="text-ink-3 text-[12.5px] leading-snug">
          El mismo presupuesto se puede mandar a varios clientes: un borrador idéntico por cliente,
          cada uno con su número al aprobarlo.
        </p>
      ) : null}

      {items.length > 0 ? (
        <ul className="divide-line-2 -mx-4 -mb-4 divide-y" aria-label="Copias de este presupuesto">
          {items.map((copy) => (
            <CopyRow key={copy.id} copy={copy} />
          ))}
        </ul>
      ) : null}

      {choosing ? (
        <CopyForClientsDialog
          quote={quote}
          taken={taken}
          onClose={() => setChoosing(false)}
          onDone={(created) => {
            setChoosing(false);
            setMade(created);
          }}
        />
      ) : null}
    </Card>
  );
}

/** One copy: who it is for, where it stands, and the way to it. */
function CopyRow({ copy }: { copy: Quote }) {
  // Only an approved copy carries the frozen name; a draft points at a card that has to
  // be read. Cached by React Query, so four rows for the same customer are one request.
  const client = useClient(copy.client_name ? null : copy.client_id);
  const name = copy.client_name ?? client.data?.name ?? (copy.client_id ? '…' : 'Sin cliente');
  return (
    <li>
      <Link
        to={`/presupuestos/${copy.id}`}
        className="hover:bg-paper-2 flex items-center justify-between gap-3 px-4 py-2.5"
      >
        <span className="min-w-0">
          <span className="text-ink block truncate text-[14px] font-medium">{name}</span>
          <span className="text-ink-3 block text-[12.5px]">{copy.quote_number ?? 'Borrador'}</span>
        </span>
        <QuoteStatusBadge status={copy.status} />
      </Link>
    </li>
  );
}

/**
 * The customers to hand the quote to: a search, the matches with a tick, and the chosen
 * ones as chips above the button. The quote's own customer and those who already have an
 * open copy are shown but cannot be ticked: the API would refuse the first and return
 * the second, and a disabled row says why better than an error afterwards.
 */
function CopyForClientsDialog({
  quote,
  taken,
  onClose,
  onDone,
}: {
  quote: Quote;
  taken: Set<string>;
  onClose: () => void;
  onDone: (created: Quote[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Client[]>([]);
  const results = useClientList({ search: useDebounced(search).trim(), limit: 20 });
  const copy = useCopyForClients(quote.id);
  const busy = copy.isPending;

  function toggle(client: Client) {
    setChosen((current) =>
      current.some((each) => each.id === client.id)
        ? current.filter((each) => each.id !== client.id)
        : [...current, client],
    );
  }

  function reasonNotToPick(client: Client): string | null {
    if (client.id === quote.client_id) return 'es el cliente de este presupuesto';
    if (taken.has(client.id)) return 'ya tiene un borrador';
    return null;
  }

  return (
    <Dialog
      title="Copiar para otros clientes"
      description="Un borrador idéntico por cliente. Cada uno se aprueba por separado y lleva su número."
      onClose={busy ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            icon={<CopyIcon size={16} />}
            disabled={busy || chosen.length === 0}
            onClick={() =>
              copy.mutate(
                chosen.map((each) => each.id),
                { onSuccess: (made) => onDone(made.items) },
              )
            }
          >
            {busy
              ? 'Creando…'
              : chosen.length <= 1
                ? 'Crear el borrador'
                : `Crear ${chosen.length} borradores`}
          </Button>
        </>
      }
    >
      {chosen.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Clientes elegidos">
          {chosen.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => toggle(client)}
                className="bg-accent-soft text-accent-2 ring-accent/30 inline-flex h-7 items-center gap-1 rounded-full pr-1.5 pl-2.5 text-[13px] font-medium ring-1 ring-inset"
                aria-label={`Quitar ${client.name}`}
              >
                {client.name}
                <XIcon size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="relative">
        <SearchIcon
          size={16}
          className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          type="search"
          aria-label="Buscar cliente"
          placeholder="Nombre, código o NIF"
          className="field w-full pl-9"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {results.isPending ? <Spinner label="Buscando clientes" /> : null}
      {results.isError ? (
        <ErrorMessage error={results.error}>No se pudo buscar el cliente</ErrorMessage>
      ) : null}

      {results.data && results.data.items.length > 0 ? (
        <ul className="divide-line-2 border-line bg-card divide-y rounded-md border">
          {results.data.items.map((client) => {
            const reason = reasonNotToPick(client);
            const ticked = chosen.some((each) => each.id === client.id);
            return (
              <li key={client.id}>
                <label
                  className={
                    reason
                      ? 'flex cursor-not-allowed items-center gap-3 px-3 py-2 opacity-60'
                      : 'hover:bg-paper-2 flex items-center gap-3 px-3 py-2'
                  }
                >
                  <input
                    type="checkbox"
                    className="accent-accent size-4"
                    checked={ticked}
                    disabled={reason !== null || busy}
                    onChange={() => toggle(client)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="text-ink block text-[14.5px] leading-5 font-medium">
                      {client.name}
                    </span>
                    <span className="text-ink-3 block text-[12.5px] leading-4">
                      {reason ?? clientSubtitle(client)}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}

      {results.data && results.data.items.length === 0 ? (
        <p className="text-ink-3 text-[13.5px]">
          {search.trim()
            ? 'Ningún cliente con ese nombre, código ni NIF.'
            : 'Escribe para buscar un cliente.'}
        </p>
      ) : null}

      {copy.isError ? (
        <ErrorMessage error={copy.error}>No se pudieron crear los borradores</ErrorMessage>
      ) : null}
    </Dialog>
  );
}
